import { useEffect, useMemo, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import TableRowsRoundedIcon from '@mui/icons-material/TableRowsRounded'
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import FlightRoundedIcon from '@mui/icons-material/FlightRounded'
import {
  adminService,
  type AttendeeRoster,
  type AttendeeRow,
  type AttendeeWritePayload,
  type GuestTravel,
  type TagPair,
} from '../../services/adminService'
import { downloadBlob } from '../download'
import { EmptyState, LoadingState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'
import { confirmDelete } from '../confirm'

const STATUS: Record<string, { label: string; color: string; bg: string }> = {
  invited: { label: 'Invited', color: '#B5611A', bg: 'rgba(228,119,32,0.15)' },
  activated: { label: 'Activated', color: '#2e7d32', bg: 'rgba(46,125,50,0.12)' },
}

const tagLabel = (t: TagPair) => `${t.key}: ${t.value}`

export default function AttendeesManager({ eventId }: { eventId: string }) {
  const { notify } = useToast()
  const [rows, setRows] = useState<AttendeeRow[]>([])
  const [loading, setLoading] = useState(true)
  const [addOpen, setAddOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [rosterOpen, setRosterOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [editing, setEditing] = useState<AttendeeRow | null>(null)
  const [bulkOpen, setBulkOpen] = useState(false)

  const exportRoster = async () => {
    try {
      const blob = await adminService.exportAttendeeRoster(eventId)
      downloadBlob(blob, 'attendee-roster.csv')
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  const load = () =>
    adminService
      .listAttendees(eventId)
      .then((r) => {
        setRows(r)
        setSelected(new Set())
      })
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((a) => {
      const hay = [
        a.email,
        a.fullName,
        a.company,
        a.track,
        a.attendeeType,
        ...a.tags.map(tagLabel),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return hay.includes(q)
    })
  }, [rows, query])

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((a) => selected.has(a.id))
  const someFilteredSelected = filtered.some((a) => selected.has(a.id))

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (allFilteredSelected) filtered.forEach((a) => next.delete(a.id))
      else filtered.forEach((a) => next.add(a.id))
      return next
    })
  }
  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  const invite = async (a: AttendeeRow) => {
    try {
      await adminService.inviteAttendee(eventId, a.id)
      notify(`Invitation sent to ${a.email}`)
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  const regInvite = async (a: AttendeeRow) => {
    try {
      await adminService.registrationInviteAttendee(eventId, a.id)
      notify(`Registration invite sent to ${a.email}`)
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  const [invitingAll, setInvitingAll] = useState(false)
  const inviteAll = async () => {
    if (
      !window.confirm(
        `Email a registration link to all ${rows.length} attendee${
          rows.length === 1 ? '' : 's'
        }?`,
      )
    )
      return
    setInvitingAll(true)
    try {
      const res = await adminService.registrationInviteAll(eventId)
      notify(`Sending registration invites to ${res.count} attendee${res.count === 1 ? '' : 's'}…`)
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setInvitingAll(false)
    }
  }

  const remove = async (a: AttendeeRow) => {
    if (!(await confirmDelete(`Remove ${a.email} from the guest list?`))) return
    try {
      await adminService.deleteAttendee(eventId, a.id)
      notify('Attendee removed')
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  if (loading) return <LoadingState />

  const selectedRows = rows.filter((a) => selected.has(a.id))

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <TextField
          size="small"
          placeholder="Search name, email, company, track, tag…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<TableRowsRoundedIcon />}
            onClick={() => setRosterOpen(true)}
            disabled={rows.length === 0}
          >
            Roster
          </Button>
          <Button
            variant="outlined"
            startIcon={<HowToRegRoundedIcon />}
            onClick={inviteAll}
            disabled={invitingAll || rows.length === 0}
          >
            {invitingAll ? 'Sending…' : 'Invite all to register'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadFileRoundedIcon />}
            onClick={() => setImportOpen(true)}
          >
            Import
          </Button>
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={() => setAddOpen(true)}
          >
            Add attendee
          </Button>
        </Stack>
      </Stack>

      {rows.length === 0 ? (
        <EmptyState
          title="No attendees yet"
          description="Add invited guests so they can sign in with a magic link."
        />
      ) : (
        <>
          {selected.size > 0 && (
            <Toolbar
              disableGutters
              sx={{
                px: 2,
                mb: 1,
                borderRadius: 2,
                bgcolor: 'action.selected',
                minHeight: '52px !important',
                gap: 1,
              }}
            >
              <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }}>
                {selected.size} selected
              </Typography>
              <Button
                size="small"
                variant="contained"
                startIcon={<LocalOfferRoundedIcon />}
                onClick={() => setBulkOpen(true)}
              >
                Edit selected
              </Button>
              <Button size="small" onClick={() => setSelected(new Set())}>
                Clear
              </Button>
            </Toolbar>
          )}

          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allFilteredSelected}
                      indeterminate={!allFilteredSelected && someFilteredSelected}
                      onChange={toggleAll}
                    />
                  </TableCell>
                  <TableCell>Name / email</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Track</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Tags</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((a) => {
                  const s = STATUS[a.inviteStatus] ?? STATUS.invited
                  return (
                    <TableRow key={a.id} hover selected={selected.has(a.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.has(a.id)}
                          onChange={() => toggleOne(a.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>
                          {a.fullName || a.email}
                        </Typography>
                        {a.fullName && (
                          <Typography variant="caption" color="text.secondary">
                            {a.email}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{a.company || '—'}</TableCell>
                      <TableCell>{a.track || '—'}</TableCell>
                      <TableCell>{a.attendeeType || '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {a.tags.length === 0
                            ? '—'
                            : a.tags.map((t) => (
                                <Chip
                                  key={`${t.key}=${t.value}`}
                                  label={tagLabel(t)}
                                  size="small"
                                  sx={{ height: 20, fontSize: '0.68rem' }}
                                />
                              ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={s.label}
                          size="small"
                          icon={
                            a.inviteStatus === 'activated' ? (
                              <CheckCircleRoundedIcon sx={{ fontSize: 15 }} />
                            ) : undefined
                          }
                          sx={{
                            height: 20,
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            color: s.color,
                            bgcolor: s.bg,
                            '& .MuiChip-icon': { color: s.color },
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => setEditing(a)}>
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Send / resend sign-in link">
                            <IconButton size="small" onClick={() => invite(a)}>
                              <SendRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Send a direct registration link">
                            <IconButton size="small" onClick={() => regInvite(a)}>
                              <HowToRegRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove">
                            <IconButton size="small" color="error" onClick={() => remove(a)}>
                              <DeleteRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )
                })}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        No attendees match “{query}”.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {addOpen && (
        <AddAttendeeDialog
          eventId={eventId}
          onClose={() => setAddOpen(false)}
          onSaved={() => {
            setAddOpen(false)
            load()
          }}
        />
      )}
      {editing && (
        <EditAttendeeDialog
          eventId={eventId}
          attendee={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            load()
          }}
        />
      )}
      {bulkOpen && (
        <BulkEditDialog
          eventId={eventId}
          attendees={selectedRows}
          onClose={() => setBulkOpen(false)}
          onSaved={() => {
            setBulkOpen(false)
            load()
          }}
        />
      )}
      {importOpen && (
        <ImportAttendeesDialog
          eventId={eventId}
          onClose={() => setImportOpen(false)}
          onSaved={() => {
            setImportOpen(false)
            load()
          }}
        />
      )}
      {rosterOpen && (
        <RosterDialog
          eventId={eventId}
          onClose={() => setRosterOpen(false)}
          onExport={exportRoster}
        />
      )}
    </Box>
  )
}

/** Editable list of key/value tags (chips + add row). */
function TagsEditor({
  tags,
  onChange,
}: {
  tags: TagPair[]
  onChange: (tags: TagPair[]) => void
}) {
  const [key, setKey] = useState('Group')
  const [value, setValue] = useState('')
  const add = () => {
    const k = key.trim()
    const v = value.trim()
    if (!k || !v) return
    if (tags.some((t) => t.key === k && t.value === v)) {
      setValue('')
      return
    }
    onChange([...tags, { key: k, value: v }])
    setValue('')
  }
  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        Tags
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {tags.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No tags
          </Typography>
        )}
        {tags.map((t) => (
          <Chip
            key={`${t.key}=${t.value}`}
            label={tagLabel(t)}
            size="small"
            onDelete={() =>
              onChange(tags.filter((x) => !(x.key === t.key && x.value === t.value)))
            }
          />
        ))}
      </Box>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          label="Key"
          size="small"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          sx={{ width: 140 }}
        />
        <TextField
          label="Value"
          size="small"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              add()
            }
          }}
          sx={{ flex: 1 }}
        />
        <Button onClick={add} disabled={!key.trim() || !value.trim()}>
          Add
        </Button>
      </Stack>
    </Stack>
  )
}

function EditAttendeeDialog({
  eventId,
  attendee,
  onClose,
  onSaved,
}: {
  eventId: string
  attendee: AttendeeRow
  onClose: () => void
  onSaved: () => void
}) {
  const { notify } = useToast()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    fullName: attendee.fullName ?? '',
    company: attendee.company ?? '',
    track: attendee.track ?? '',
    attendeeType: attendee.attendeeType ?? '',
  })
  const [tags, setTags] = useState<TagPair[]>(attendee.tags)
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }))

  const save = async () => {
    setSaving(true)
    try {
      await adminService.updateAttendee(eventId, attendee.id, {
        fullName: form.fullName,
        company: form.company,
        track: form.track,
        attendeeType: form.attendeeType,
        tags,
      })
      notify('Attendee updated')
      onSaved()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit {attendee.email}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Full name" fullWidth value={form.fullName} onChange={(e) => set('fullName', e.target.value)} />
          <Stack direction="row" spacing={2}>
            <TextField label="Company" fullWidth value={form.company} onChange={(e) => set('company', e.target.value)} />
            <TextField label="Track" fullWidth value={form.track} onChange={(e) => set('track', e.target.value)} />
          </Stack>
          <TextField label="Type" fullWidth value={form.attendeeType} onChange={(e) => set('attendeeType', e.target.value)} />
          <TagsEditor tags={tags} onChange={setTags} />
          <GuestTravelSection eventId={eventId} attendee={attendee} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
     </Dialog>
   )
 }

/**
 * Collapsible "Travel & lodging" section for special guests whose flights/hotel
 * the organizer arranges. Loads lazily on expand; saves independently of the
 * main attendee fields; can send the personalized travel-details email.
 */
function GuestTravelSection({
  eventId,
  attendee,
}: {
  eventId: string
  attendee: AttendeeRow
}) {
  const { notify } = useToast()
  const [loaded, setLoaded] = useState(false)
  const [saving, setSaving] = useState(false)
  const [sending, setSending] = useState(false)
  const [t, setT] = useState<GuestTravel>({})
  // Whether the "Travel details" (guest_travel) email exists and is enabled —
  // required for the Send button to work. Undefined until loaded.
  const [emailReady, setEmailReady] = useState<boolean | undefined>(undefined)

  const load = () => {
    if (loaded) return
    Promise.all([
      adminService.getGuestTravel(eventId, attendee.id),
      adminService.listEmailTemplates(eventId).catch(() => []),
    ])
      .then(([data, templates]) => {
        setT(data)
        const tpl = templates.find((x) => x.kind === 'guest_travel')
        setEmailReady(Boolean(tpl && tpl.enabled && tpl.blocks.length > 0))
        setLoaded(true)
      })
      .catch((e) => notify(apiError(e), 'error'))
  }

  const set = (k: keyof GuestTravel, v: string) => setT((p) => ({ ...p, [k]: v }))
  const str = (k: keyof GuestTravel) => (t[k] as string) ?? ''

  const save = async () => {
    setSaving(true)
    try {
      const saved = await adminService.saveGuestTravel(eventId, attendee.id, t)
      setT(saved)
      notify('Travel details saved')
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  const sendEmail = async () => {
    setSending(true)
    try {
      const res = await adminService.sendGuestTravelEmail(eventId, attendee.id)
      notify(`Travel details emailed to ${res.to}`)
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSending(false)
    }
  }

  return (
    <Accordion disableGutters onChange={(_, expanded) => expanded && load()} sx={{ mt: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <FlightRoundedIcon fontSize="small" color="action" />
          <Typography fontWeight={600}>Travel &amp; lodging (special guest)</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {!loaded ? (
          <LoadingState />
        ) : (
          <Stack spacing={2}>
            <Typography variant="caption" color="text.secondary">
              For guests whose flights and hotel you arrange. All fields optional.
            </Typography>

            <Divider textAlign="left"><Typography variant="caption">Flights</Typography></Divider>
            <TextField label="Airline" size="small" fullWidth value={str('airline')} onChange={(e) => set('airline', e.target.value)} />
            <Stack direction="row" spacing={2}>
              <TextField label="Arrival flight" size="small" fullWidth value={str('arrivalFlight')} onChange={(e) => set('arrivalFlight', e.target.value)} />
              <TextField label="Arrives (date/time)" size="small" fullWidth value={str('arrivalDatetime')} onChange={(e) => set('arrivalDatetime', e.target.value)} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Departure flight" size="small" fullWidth value={str('departureFlight')} onChange={(e) => set('departureFlight', e.target.value)} />
              <TextField label="Departs (date/time)" size="small" fullWidth value={str('departureDatetime')} onChange={(e) => set('departureDatetime', e.target.value)} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Seat preference" size="small" fullWidth value={str('seatPreference')} onChange={(e) => set('seatPreference', e.target.value)} />
            </Stack>

            <Divider textAlign="left"><Typography variant="caption">Traveler programs</Typography></Divider>
            <Stack direction="row" spacing={2}>
              <TextField label="Frequent flyer #" size="small" fullWidth value={str('frequentFlyerNumber')} onChange={(e) => set('frequentFlyerNumber', e.target.value)} />
              <TextField label="Known traveler # (TSA PreCheck / KTN)" size="small" fullWidth value={str('knownTravelerNumber')} onChange={(e) => set('knownTravelerNumber', e.target.value)} />
            </Stack>

            <Divider textAlign="left"><Typography variant="caption">Hotel</Typography></Divider>
            <TextField label="Hotel name" size="small" fullWidth value={str('hotelName')} onChange={(e) => set('hotelName', e.target.value)} />
            <TextField label="Hotel address" size="small" fullWidth value={str('hotelAddress')} onChange={(e) => set('hotelAddress', e.target.value)} />
            <Stack direction="row" spacing={2}>
              <TextField label="Confirmation #" size="small" fullWidth value={str('hotelConfirmation')} onChange={(e) => set('hotelConfirmation', e.target.value)} />
              <TextField label="Check-in" size="small" fullWidth value={str('checkInDate')} onChange={(e) => set('checkInDate', e.target.value)} />
              <TextField label="Check-out" size="small" fullWidth value={str('checkOutDate')} onChange={(e) => set('checkOutDate', e.target.value)} />
            </Stack>

            <Divider textAlign="left"><Typography variant="caption">Ground transport &amp; notes</Typography></Divider>
            <TextField label="Car service" size="small" fullWidth value={str('carService')} onChange={(e) => set('carService', e.target.value)} />
            <TextField label="Notes" size="small" fullWidth multiline minRows={2} value={str('notes')} onChange={(e) => set('notes', e.target.value)} />

            {emailReady === false && (
              <Alert severity="info" sx={{ mt: 0.5 }}>
                To email travel details, first create the{' '}
                <strong>“Travel details (special guest)”</strong> email in the{' '}
                <strong>Emails</strong> tab, add a <strong>Travel details</strong>{' '}
                block (or <code>{'{{travel_*}}'}</code> tokens), and enable it.
              </Alert>
            )}

            <Stack direction="row" spacing={1}>
              <Button variant="contained" size="small" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save travel'}
              </Button>
              <Button
                size="small"
                startIcon={<SendRoundedIcon />}
                onClick={sendEmail}
                disabled={sending || emailReady === false}
                title={
                  emailReady === false
                    ? 'Set up the “Travel details” email in the Emails tab first'
                    : 'Sends the guest their personalized travel details email'
                }
              >
                {sending ? 'Sending…' : 'Send travel email'}
              </Button>
            </Stack>
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  )
}

function BulkFieldRow({
  label,
  value,
  onValue,
  cleared,
  onCleared,
}: {
  label: string
  value: string
  onValue: (v: string) => void
  cleared: boolean
  onCleared: (v: boolean) => void
}) {
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <TextField
        label={label}
        size="small"
        fullWidth
        value={value}
        disabled={cleared}
        placeholder="Leave blank to keep unchanged"
        onChange={(e) => onValue(e.target.value)}
      />
      <FormControlLabel
        control={<Switch checked={cleared} onChange={(e) => onCleared(e.target.checked)} />}
        label="Clear"
      />
    </Stack>
  )
}

function BulkEditDialog({
  eventId,
  attendees,
  onClose,
  onSaved,
}: {
  eventId: string
  attendees: AttendeeRow[]
  onClose: () => void
  onSaved: () => void
}) {
  const { notify } = useToast()
  const [saving, setSaving] = useState(false)
  // Each field: a value to overwrite with (blank = leave unchanged) + a Clear toggle.
  const [company, setCompany] = useState('')
  const [track, setTrack] = useState('')
  const [attendeeType, setAttendeeType] = useState('')
  const [clearCompany, setClearCompany] = useState(false)
  const [clearTrack, setClearTrack] = useState(false)
  const [clearType, setClearType] = useState(false)
  const [addTags, setAddTags] = useState<TagPair[]>([])
  const [removeTags, setRemoveTags] = useState<TagPair[]>([])

  const n = attendees.length

  const save = async () => {
    const set: { company?: string; track?: string; attendeeType?: string } = {}
    if (!clearCompany && company.trim()) set.company = company.trim()
    if (!clearTrack && track.trim()) set.track = track.trim()
    if (!clearType && attendeeType.trim()) set.attendeeType = attendeeType.trim()
    const clear: string[] = []
    if (clearCompany) clear.push('company')
    if (clearTrack) clear.push('track')
    if (clearType) clear.push('attendeeType')

    if (
      Object.keys(set).length === 0 &&
      clear.length === 0 &&
      addTags.length === 0 &&
      removeTags.length === 0
    ) {
      notify('Nothing to apply', 'error')
      return
    }
    setSaving(true)
    try {
      const res = await adminService.bulkUpdateAttendees(eventId, {
        attendeeIds: attendees.map((a) => a.id),
        set: Object.keys(set).length ? set : undefined,
        clear: clear.length ? clear : undefined,
        addTags: addTags.length ? addTags : undefined,
        removeTags: removeTags.length ? removeTags : undefined,
      })
      notify(`Updated ${res.updated} attendee${res.updated === 1 ? '' : 's'}`)
      onSaved()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit {n} attendee{n === 1 ? '' : 's'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Blank fields are left unchanged. A value overwrites that field for all
          selected; “Clear” wipes it.
        </Typography>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <BulkFieldRow label="Company" value={company} onValue={setCompany} cleared={clearCompany} onCleared={setClearCompany} />
          <BulkFieldRow label="Track" value={track} onValue={setTrack} cleared={clearTrack} onCleared={setClearTrack} />
          <BulkFieldRow label="Type" value={attendeeType} onValue={setAttendeeType} cleared={clearType} onCleared={setClearType} />
          <Box>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Add tags (to all selected)
            </Typography>
            <TagsEditor tags={addTags} onChange={setAddTags} />
          </Box>
          <Box>
            <Typography variant="subtitle2">Remove tags (from all selected)</Typography>
            <TagsEditor tags={removeTags} onChange={setRemoveTags} />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Applying…' : `Apply to ${n}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function RosterDialog({
  eventId,
  onClose,
  onExport,
}: {
  eventId: string
  onClose: () => void
  onExport: () => void
}) {
  const { notify } = useToast()
  const [roster, setRoster] = useState<AttendeeRoster | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    adminService
      .getAttendeeRoster(eventId)
      .then(setRoster)
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  return (
    <Dialog open onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <span>Attendee roster</span>
          <Button
            variant="contained"
            size="small"
            startIcon={<DownloadRoundedIcon />}
            onClick={onExport}
          >
            Export CSV
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Every attendee with their registration selections and form answers.
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <LoadingState />
        ) : !roster || roster.rows.length === 0 ? (
          <EmptyState title="No attendees yet" description="" />
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {roster.columns.map((c) => (
                    <TableCell
                      key={c.key}
                      sx={{
                        whiteSpace: 'nowrap',
                        fontWeight: 700,
                        color:
                          c.group === 'attendee'
                            ? 'text.primary'
                            : 'text.secondary',
                      }}
                    >
                      {c.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {roster.rows.map((row, i) => (
                  <TableRow key={i} hover>
                    {roster.columns.map((c) => (
                      <TableCell key={c.key} sx={{ whiteSpace: 'nowrap' }}>
                        {row[c.key] ?? ''}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

function AddAttendeeDialog({
  eventId,
  onClose,
  onSaved,
}: {
  eventId: string
  onClose: () => void
  onSaved: () => void
}) {
  const { notify } = useToast()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    email: '',
    fullName: '',
    company: '',
    track: '',
  })
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }))

  const save = async () => {
    if (!form.email.trim()) {
      notify('Email is required', 'error')
      return
    }
    const payload: AttendeeWritePayload = {
      email: form.email.trim(),
      fullName: form.fullName || undefined,
      company: form.company || undefined,
      track: form.track || undefined,
    }
    setSaving(true)
    try {
      await adminService.createAttendee(eventId, payload)
      notify('Attendee added')
      onSaved()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add attendee</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Email"
            type="email"
            required
            fullWidth
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
          />
          <TextField
            label="Full name"
            fullWidth
            value={form.fullName}
            onChange={(e) => set('fullName', e.target.value)}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Company"
              fullWidth
              value={form.company}
              onChange={(e) => set('company', e.target.value)}
            />
            <TextField
              label="Track"
              fullWidth
              value={form.track}
              onChange={(e) => set('track', e.target.value)}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * Parse pasted lines. Accepts either a bare email per line, or
 * `email, Full Name, Company, Track` (CSV-ish). A header row containing
 * "email" is ignored.
 */
function parseImport(text: string): AttendeeWritePayload[] {
  const out: AttendeeWritePayload[] = []
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim()
    if (!line) continue
    const parts = line.split(',').map((p) => p.trim())
    const email = parts[0]
    if (!email || !email.includes('@')) continue // skip headers / junk
    out.push({
      email,
      fullName: parts[1] || undefined,
      company: parts[2] || undefined,
      track: parts[3] || undefined,
    })
  }
  return out
}

function ImportAttendeesDialog({
  eventId,
  onClose,
  onSaved,
}: {
  eventId: string
  onClose: () => void
  onSaved: () => void
}) {
  const { notify } = useToast()
  const [text, setText] = useState('')
  const [saving, setSaving] = useState(false)
  const parsed = parseImport(text)

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-selecting the same file
    if (!file) return
    try {
      const content = await file.text()
      setText((t) => (t ? `${t}\n${content}` : content))
    } catch {
      notify('Could not read file', 'error')
    }
  }

  const save = async () => {
    if (parsed.length === 0) {
      notify('Add at least one valid email', 'error')
      return
    }
    setSaving(true)
    try {
      const res = await adminService.importAttendees(eventId, parsed)
      notify(`Imported: ${res.created} added, ${res.updated} updated`)
      onSaved()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import attendees</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Paste one attendee per line, or upload a CSV. Use just an email, or
          <code> email, Full Name, Company, Track</code>.
        </Typography>
        <Button component="label" variant="outlined" size="small" sx={{ mb: 2 }}>
          Upload CSV
          <input type="file" accept=".csv,text/csv" hidden onChange={onFile} />
        </Button>
        <TextField
          fullWidth
          multiline
          minRows={8}
          placeholder={'jordan@example.com, Jordan Avery, Northbank, Executive Briefing\nsam@example.com'}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {parsed.length} valid {parsed.length === 1 ? 'entry' : 'entries'} detected
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving || parsed.length === 0}>
          {saving ? 'Importing…' : `Import ${parsed.length || ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
